import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Popup } from "semantic-ui-react";

import { DATE_FORMATS_FNS, ReactTable } from "components";
import "modules/WebhooksDashboard/components/WebhooksErrorsModal/WebhooksErrorsModal.scss";
import { useWebhookErrorsQuery } from "modules/WebhooksDashboard/hooks";
import { PAGE_SIZE } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

import type { OnChangeFn, PaginationState } from "@tanstack/react-table";
import type { WebhookError } from "models";

type WebhooksErrorsTableProps = {
  webhook_id: number | null;
};

const columnHelper = createColumnHelper<WebhookError>();

export const WebhooksErrorsTable = ({ webhook_id }: WebhooksErrorsTableProps) => {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE.PageSize25);

  const { data: paginatedWebhookErrors, isFetching } = useWebhookErrorsQuery({ page, webhook_id, page_size: pageSize });

  const handlePageSizeChange = (value: PAGE_SIZE) => setPageSize(value);
  const handlePaginationChange: OnChangeFn<PaginationState> = updater => {
    const newState = typeof updater === "function" ? updater({ pageIndex: page - 1, pageSize }) : updater;
    setPage(newState.pageIndex + 1);
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "date",
        size: 250,
        header: () => <span className="no-wrap-text"> {t("v8_date", "Date")} </span>,
        cell: ({ row: { original } }) => <span> {formatDate(original.created_on, DATE_FORMATS_FNS.dateMonthYearTimeWithSeconds)} </span>
      }),

      columnHelper.accessor(row => row, {
        id: "reason",
        size: 100,
        header: () => <span className="no-wrap-text"> {t("v8_reason", "Reason")} </span>,
        cell: ({ row: { original } }) => <Popup hoverable content={original.reason} trigger={<p className="WebhooksErrorsModal__reason-ellipsis">{original.reason}</p>} />
      })
    ],
    [t]
  );

  return (
    <div className="WebhooksErrorsModal">
      <ReactTable
        isFetching={isFetching}
        columns={columns}
        data={paginatedWebhookErrors?.items}
        pageCount={paginatedWebhookErrors?.nb_pages}
        pageSize={pageSize}
        totalItems={paginatedWebhookErrors?.nb_items}
        pagination={(paginatedWebhookErrors?.nb_pages ?? 0) > 1 ? { pageIndex: page - 1, pageSize } : undefined}
        onChangePageSize={handlePageSizeChange}
        onPaginationChange={handlePaginationChange}
      />
    </div>
  );
};
